/**
 * Tag vocabularies for the Pitch Tagging panel. Values match the backend
 * activity `result` fields (see __tests__/fixtures/game-full-response.json).
 */

import { Sport } from "../types/gameFull";

export type TagOption = {
  value: string;
  label: string;
  hotkey?: string;
};

const BASEBALL_PITCH_TYPE_OPTIONS: TagOption[] = [
  { value: "Fastball", label: "4S Fastball", hotkey: "4" },
  // TODO: Confirm backend value for 2S Fastball
  { value: "TwoSeamFastball", label: "2S Fastball", hotkey: "2" },
  { value: "Cutter", label: "Cutter", hotkey: "T" },
  { value: "Changeup", label: "Changeup", hotkey: "C" },
  { value: "Curveball", label: "Curveball", hotkey: "V" },
  { value: "Slider", label: "Slider", hotkey: "S" },
  { value: "Splitter", label: "Splitter", hotkey: "P" },
  { value: "Knuckleball", label: "Knuckle", hotkey: "K" },
  { value: "Sinker", label: "Sinker", hotkey: "N" },
  // TODO: Confirm backend value for Sweeper
  { value: "Sweeper", label: "Sweeper", hotkey: "E" },
];

// Same values and keys as the V1 tagger's softball step
const SOFTBALL_PITCH_TYPE_OPTIONS: TagOption[] = [
  { value: "Fastball", label: "Fastball", hotkey: "F" },
  { value: "Dropball", label: "Dropball", hotkey: "D" },
  { value: "Riseball", label: "Riseball", hotkey: "R" },
  { value: "Curveball", label: "Curveball", hotkey: "V" },
  { value: "Screwball", label: "Screwball", hotkey: "S" },
  { value: "LowRise", label: "Low Rise", hotkey: "L" },
  { value: "Changeup", label: "Changeup", hotkey: "C" },
  { value: "DropCurve", label: "Drop Curve", hotkey: "P" },
];

export const PITCH_TYPE_OPTIONS: Record<Sport, TagOption[]> = {
  baseball: BASEBALL_PITCH_TYPE_OPTIONS,
  softball: SOFTBALL_PITCH_TYPE_OPTIONS,
};

// Same keys as the V1 tagger
export const PITCH_RESULT_OPTIONS: TagOption[] = [
  { value: "Ball", label: "Ball", hotkey: "B" },
  { value: "CalledStrike", label: "Strike L", hotkey: "L" },
  { value: "SwingingStrike", label: "Strike SW", hotkey: "S" },
  { value: "Foul", label: "Foul", hotkey: "F" },
  { value: "InPlay", label: "In Play", hotkey: "I" },
  { value: "HitByPitch", label: "HBP", hotkey: "H" },
  { value: "CatchersInt", label: "Catcher Int", hotkey: "C" },
  { value: "NotAPitch", label: "Ignore", hotkey: "N" },
];

// Ordered row-by-row for a 3-column grid
export const PLAY_RESULT_OPTIONS: TagOption[] = [
  { value: "Out", label: "Out(s)", hotkey: "O" },
  { value: "Single", label: "1B", hotkey: "1" },
  { value: "StrikeoutSwinging", label: "K", hotkey: "K" },
  { value: "Sacrifice", label: "SAC", hotkey: "S" },
  { value: "Double", label: "2B", hotkey: "2" },
  { value: "StrikeoutLooking", label: "ꓘ", hotkey: "L" },
  { value: "FieldersChoice", label: "FC", hotkey: "F" },
  { value: "Triple", label: "3B", hotkey: "3" },
  { value: "Walk", label: "BB", hotkey: "B" },
  { value: "Error", label: "Error", hotkey: "E" },
  { value: "HomeRun", label: "HR", hotkey: "H" },
  // Display only: never sent (see toOmnibusPlayResult)
  { value: "HitByPitch", label: "HBP" },
];

export const CONTACT_TYPE_OPTIONS: TagOption[] = [
  { value: "GroundBall", label: "Groundball", hotkey: "G" },
  { value: "LineDrive", label: "Line Drive", hotkey: "L" },
  { value: "FlyBall", label: "Fly Ball", hotkey: "F" },
  { value: "Popup", label: "Popup", hotkey: "P" },
  { value: "Bunt", label: "Bunt", hotkey: "B" },
];

const IN_PLAY_RESULTS = [
  "Out",
  "Single",
  "Double",
  "Triple",
  "HomeRun",
  "Sacrifice",
  "FieldersChoice",
  "Error",
];

/**
 * Play results allowed for a given pitch result (spec 3.3). A hit by pitch's
 * play result is display only: the tag sends its pitch result alone.
 */
export const getAllowedPlayResults = (pitchResult: string | null) => {
  switch (pitchResult) {
    case "Ball":
      return ["Out", "Walk"];
    case "CalledStrike":
      return ["Out", "StrikeoutLooking"];
    case "SwingingStrike":
      return ["Out", "StrikeoutSwinging"];
    case "Foul":
      return ["Out", "Sacrifice"];
    case "InPlay":
      return IN_PLAY_RESULTS;
    case "HitByPitch":
      return ["HitByPitch"];
    case "CatchersInt":
      return ["Error"];
    default:
      return [];
  }
};

export const isContactTypeAllowed = (pitchResult: string | null) =>
  pitchResult === "InPlay" || pitchResult === "Foul";

/** Helper text shown above the play result options. */
export const getPlayResultHelperText = (pitchResult: string | null) => {
  switch (pitchResult) {
    case "Ball":
    case "CalledStrike":
    case "SwingingStrike":
    case "Foul":
      return "Leave blank if this pitch did not end the at bat";
    case "InPlay":
      return "Please make a play result selection";
    default:
      return null;
  }
};

/** Whether the pitch ended the at bat: a play result, or a hit by pitch. */
export const endsAtBat = (
  pitchResult: string | null,
  playResult: string | null,
) => !!playResult || pitchResult === "HitByPitch";

/** Play results that record an out. */
export const OUT_PLAY_RESULTS = [
  "Out",
  "StrikeoutSwinging",
  "StrikeoutLooking",
  "Sacrifice",
  "FieldersChoice",
];

/**
 * Play result to pre-select for a pitch result. HBP and Catcher Int have a
 * single outcome; ball four and strike three come from the count going into the
 * pitch, so a corrected earlier pitch gets them too.
 */
export const getAutoPlayResult = (
  pitchResult: string | null,
  countBefore: { balls: number; strikes: number },
): string | null => {
  switch (pitchResult) {
    case "HitByPitch":
      return "HitByPitch";
    case "CatchersInt":
      return "Error";
    case "Ball":
      return countBefore.balls === 3 ? "Walk" : null;
    case "CalledStrike":
      return countBefore.strikes === 2 ? "StrikeoutLooking" : null;
    case "SwingingStrike":
      return countBefore.strikes === 2 ? "StrikeoutSwinging" : null;
    default:
      return null;
  }
};
